import type { AxiosInstance } from 'axios'
import axios from 'axios'
import { isEmpty } from 'lodash-es'
import { computed, inject, ref, type ComputedRef, type Ref } from 'vue'

import { REQUEST_DEFAULT_CANCEL_TIMEOUT } from '@lib/constants/timers.const'
import type { PitcherInfo } from '@lib/types/app'
import type { File } from '@openapi/models/File'
import type { PaginatedFileList } from '@openapi/models/PaginatedFileList'

export function useFetchMostPopularFiles() {
  const pitcherInfo = inject<ComputedRef<PitcherInfo>>(
    'pitcherInfo',
    computed(() => undefined),
  )
  const restApiAxios = inject<Ref<AxiosInstance | undefined>>('restApiAxios')
  const cachedFiles = ref<Record<string, { data: any; timestamp: number }>>({})

  async function fetchMostPopularFiles(): Promise<File[]> {
    if (!pitcherInfo.value) throw new Error('Pitcher info is not provided')
    if (!restApiAxios?.value) throw new Error('HTTP client is not initialized')

    const instanceId = pitcherInfo.value.instanceId
    const now = Date.now()

    if (cachedFiles.value[instanceId] && now - cachedFiles.value[instanceId].timestamp < 5 * 60 * 1000) {
      return cachedFiles.value[instanceId].data || []
    }

    try {
      const authorization = `Bearer ${pitcherInfo.value.accessToken}`

      const analyticsRecordsResponse = (
        await axios.get<any>(
          `${window.location.origin}/core/api/protected/analytics?type=popularity&instance_id=${instanceId}&entity_type=File`,
          {
            timeout: REQUEST_DEFAULT_CANCEL_TIMEOUT,
            headers: {
              authorization,
            },
          },
        )
      )?.data

      const analyticsRecords = analyticsRecordsResponse?.data
      const mostPopularFileIds = analyticsRecords.map((record: any) => record.what).slice(0, 12)

      if (isEmpty(mostPopularFileIds)) return []

      const response = await restApiAxios.value.get<unknown, PaginatedFileList>('files/', {
        params: {
          instance_id: instanceId,
          id__in: mostPopularFileIds.join(','),
          page_size: mostPopularFileIds.length,
        },
        timeout: REQUEST_DEFAULT_CANCEL_TIMEOUT,
      })

      const mostPopularFiles = response.results

      cachedFiles.value[instanceId] = { data: mostPopularFiles, timestamp: now }

      return mostPopularFiles || []
    } catch (e) {
      console.error('fetchMostPopularFiles: failed', e)
      return []
    }
  }

  return {
    fetchMostPopularFiles,
  }
}
